// routes/_app.$usernameRaw.collection.$collectionId.tsx
//
// `/{lang}/@username/collection/:collectionId` — the per-collection
// (a.k.a. bookmark) view of a user's saved artworks. Previously
// rendered as a shell-SSR + `dynamic({ssr:false})` swap:
// SSR shipped a `<UserProfileShell>` with placeholder avatar /
// follower counts, then the client mounted the real
// `<UserProfileContent>` (the same component that powers
// `/@user/artworks`, just with `useProfileTab()` mapping the
// `/collection/` prefix back to the `'artworks'` tab so the
// collection bookmarks section + Tabs render). The DOM diff between
// the SSR shell and the post-hydrate real tree was significant
// enough that Google saw one page (shell) while users saw another
// (real content) — a textbook cause of SEO "soft 404 / cloaking"
// flags, and a visible shell→real flash on every navigation.
//
// New behaviour: drop the `dynamic({ssr:false})` boundary and render
// the real `<UserProfileContent>` directly on both server and
// client. The parent `_app.$usernameRaw.tsx` layout already SSR-
// mounts a route-scoped `<ApolloProvider>` + `<QueryClientProvider>`
// seeded with the `GetUserInfoByUsername` document, so the child's
// `useUserProfileContext()` → `useGetBookmark()` chain renders the
// same markup on both sides (the collection list / artwork grid
// inside the page still streams in via Apollo, but it starts at
// the same skeleton on SSR and hydrate, with no provider-level
// remount in between).
//
// The loader still produces SEO meta + `noIndex` for the page; the
// `userShell` shape it used to ship to the now-removed
// `<UserProfileShell>` is also removed, since nothing reads it.
import debug from 'debug'
import UserProfileContent from 'src/components/UserProfile/UserProfileContent'
import { getI18n } from 'src/i18n/i18n.middleware'
import type { SeoServerConfig } from 'src/utils/ssr-meta/consul.server'
import {
  DEFAULT_TITLE,
  profileMeta,
  profileMetaLoader,
} from 'src/utils/ssr-meta/seo'
import type { Route } from './+types/_app.$usernameRaw.collection.$collectionId'

const log = debug('pixai:ssr-meta')

export const handle = {
  name: 'bookmark-artwork',
  showAppNav: true,
}

export async function loader({ params, request, context }: Route.LoaderArgs) {
  const { bucketString, parseUsername } =
    await import('src/utils/ssr-meta/url.server')
  const { fetchUserForSSR } =
    await import('src/utils/ssr-meta/graphql/user.server')
  const { getSeoConfig } = await import('src/utils/ssr-meta/consul.server')

  function shouldNoIndexUser(
    username: string,
    likesReceived: number,
    config: Required<SeoServerConfig>,
  ): boolean {
    const { gradualRolloutPercent, userIndexedMinimalLikes } = config
    const inRolloutBucket = bucketString(username, 100) < gradualRolloutPercent
    return inRolloutBucket && likesReceived < userIndexedMinimalLikes
  }

  const usernameRaw = params.usernameRaw
  if (!usernameRaw) {
    log('artist collection loader missing usernameRaw url=%s', request.url)
    return profileMetaLoader({ requestUrl: request.url })
  }

  const username = parseUsername(usernameRaw)
  if (!username) {
    log(
      'artist collection loader invalid usernameRaw=%s url=%s',
      usernameRaw,
      request.url,
    )
    return profileMetaLoader({ requestUrl: request.url })
  }

  const [user, seoConfig] = await Promise.all([
    fetchUserForSSR(username),
    getSeoConfig(),
  ])

  if (!user) {
    log(
      'artist collection loader user not found username=%s url=%s',
      username,
      request.url,
    )
    return profileMetaLoader({ requestUrl: request.url })
  }

  const likesReceived = user.artworkLikedCount ?? 0
  const shouldNoIndex = shouldNoIndexUser(username, likesReceived, seoConfig)

  const { t } = getI18n(context)
  const i18nTitle = user.displayName
    ? t('common:meta.title.user.home', { username: user.displayName })
    : DEFAULT_TITLE

  let avatarUrl = user.avatarSmallThumbnailMediaUrl || undefined
  if (avatarUrl) {
    avatarUrl = avatarUrl.replace('/images/avatar/', '/images/stillThumb/')
  }

  const meta = profileMetaLoader({
    requestUrl: request.url,
    title: i18nTitle || DEFAULT_TITLE,
    image: avatarUrl,
    twitterCard: avatarUrl ? 'summary' : 'summary_large_image',
  })

  return {
    ...meta,
    user,
    noIndex: shouldNoIndex,
  }
}

export function meta({ loaderData }: Route.MetaArgs) {
  return profileMeta(loaderData)
}

export default UserProfileContent
